import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '~/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';

import { createSchoolYearSchema, defaultFormValues } from './createSchoolYearSchema';
import type { CreateSchoolYearFormValues } from './createSchoolYearSchema';
import { useCreateSchoolYear } from './useCreateSchoolYear';

interface CreateSchoolYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  onSuccess?: (newSchoolYear?: { id: string; slug: string }) => void;
}

export function CreateSchoolYearDialog({ open, onOpenChange, schoolId, onSuccess }: CreateSchoolYearDialogProps) {
  const form = useForm<CreateSchoolYearFormValues>({
    resolver: zodResolver(createSchoolYearSchema),
    defaultValues: defaultFormValues,
  });

  const [createSchoolYear, { loading }] = useCreateSchoolYear();

  const onSubmit = async (data: CreateSchoolYearFormValues) => {
    try {
      const result = await createSchoolYear({
        variables: {
          schoolId,
          name: data.name,
          administrativeStartDate: data.administrativeStartDate,
          administrativeEndDate: data.administrativeEndDate,
          reenrollmentStartDate: data.reenrollmentStartDate || null,
          openEnrollmentStartDate: data.openEnrollmentStartDate || null,
        },
      });

      if (result.data?.createSchoolYear?.userErrors?.length) {
        const errors = result.data.createSchoolYear.userErrors;
        errors.forEach((error: { message: string }) => {
          form.setError('root', { message: error.message });
        });
        return;
      }

      const schoolYears = result.data?.createSchoolYear?.school?.schoolYears || [];
      const newSchoolYear = schoolYears[schoolYears.length - 1];

      form.reset(defaultFormValues);
      onOpenChange(false);
      onSuccess?.(newSchoolYear);
    } catch (error: any) {
      form.setError('root', { message: error.message });
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset(defaultFormValues);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create School Year</DialogTitle>
          <DialogDescription>
            Create a new school year for enrollment and administration.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {form.formState.errors.root && (
              <div className="text-sm text-destructive">
                {form.formState.errors.root.message}
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., 2026-2027" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="administrativeStartDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Admin Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      When contracts & tuition begin
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="administrativeEndDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Admin End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      End of administrative year
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Enrollment Dates (Optional)</p>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="reenrollmentStartDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Re-enrollment Opens</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="openEnrollmentStartDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Open Enrollment Opens</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create School Year'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateSchoolYearDialog;
